import { Component, ErrorInfo, ReactNode } from 'react';
import { Typography, Button, Container, Box } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SendIcon from '@mui/icons-material/Send';

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
    errorTime: string;
    copySuccess: boolean;
    copyError: string;
}

interface ErrorBoundaryProps {
    children: ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorTime: '',
            copySuccess: false,
            copyError: ''
        };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return { hasError: true, error, errorTime: new Date().toLocaleString() };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({ errorInfo });
    }

    copyErrorToClipboard = () => {
        const errorText = this.state.error ? this.state.error.toString() : '';
        const errorTime = this.state.errorTime;
        const componentStack = this.state.errorInfo ? this.state.errorInfo.componentStack : '';
        const fullErrorMessage = `Timestamp: ${errorTime}\nError: ${errorText}\nStack trace: ${componentStack}`;

        navigator.clipboard
            .writeText(fullErrorMessage)
            .then(() => {
                this.setState({ copySuccess: true, copyError: '' });
                setTimeout(() => this.setState({ copySuccess: false }), 3000);
            })
            .catch(() => {
                this.setState({ copyError: 'Failed to copy the error details. Please try again.' });
            });
    };

    render() {
        if (this.state.hasError) {
            return (
                <Container component="main" maxWidth="xl">
                    <Box style={{ marginTop: '2rem', padding: '2rem', textAlign: 'center' }}>
                        <ErrorOutlineIcon style={{ fontSize: '5rem', color: 'red' }} />
                        <Typography variant="h4" style={{ margin: '1rem 0 3rem' }}>
                            An error has occurred.
                        </Typography>
                        <Container component="main" maxWidth="sm">
                            <Typography variant="body1" style={{ marginBottom: '2rem' }} align="center">
                                We apologize for the inconvenience. <br />
                                Kindly report this issue to the Product Team.
                            </Typography>

                            <Box display="flex" justifyContent="center" gap="1rem">
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        this.copyErrorToClipboard();
                                    }}
                                    disabled={this.state.copySuccess}
                                >
                                    {this.state.copySuccess ? 'Copied' : 'Copy error details to clipboard'}
                                </Button>
                                <Button variant="outlined" color="primary" href="/" style={{ height: 'fit-content' }}>
                                    Go Home
                                </Button>
                            </Box>

                            <Box
                                style={{
                                    borderTop: '1px solid #ddd',
                                    borderBottom: '1px solid #ddd',
                                    marginTop: '2rem',
                                    marginBottom: '2rem',
                                    paddingTop: '2rem',
                                    paddingBottom: '2rem'
                                }}
                            >
                                <Typography variant="body1" style={{ marginBottom: '2rem' }} align="center">
                                    <strong>Your help and feedback are invaluable in the bug-reporting process.</strong>
                                </Typography>
                                <Box display="flex" justifyContent="center" gap="1rem">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        href="https://forms.gle/4smZKXYJdfrZuV2F6"
                                        style={{ height: 'fit-content' }}
                                        endIcon={<SendIcon />}
                                        target="_blank"
                                    >
                                        Report this bug
                                    </Button>
                                </Box>
                            </Box>
                            <Box style={{ color: '#595959' }}>
                                <Typography fontSize={12} align="center">
                                    <b>Error details:</b>
                                </Typography>
                                {this.state.copyError && (
                                    <Typography color="error" style={{ marginTop: '1rem' }}>
                                        {this.state.copyError}
                                    </Typography>
                                )}
                                <Typography fontSize={12} align="left">
                                    <p> {`Timestamp: ${this.state.errorTime}`}</p>
                                    <p>{this.state.error && this.state.error.toString()}</p>
                                </Typography>
                            </Box>
                        </Container>
                    </Box>
                </Container>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
